import { useEffect, useRef } from 'react'
import type { Comment } from '@/tiptap/tiptap-extensions/custom/comments/types'
import { cn } from '@/lib/utils'
import { CommentContent } from './CommentContent'
import { ConfirmDialog } from '@/components/confirm-dialog'
import { useState } from 'react'

interface CommentThreadProps {
  comment: Comment
  onResolve: (
    commentId: string,
    resolution: 'RESOLVED' | 'DISMISSED' | 'IMPLEMENTED'
  ) => void
  onDelete: (commentId: string) => void
  onReply: (parentId: string, content: string, extra?: { mentions?: string[]; richContent?: any }) => void
  onEditComment?: (commentId: string, content: string, metadata?: any) => void
  onApplySuggestion?: (commentId: string) => void
  onDismissCheck?: (commentId: string) => void
  onPromoteToTask?: (commentId: string) => void
  onClick?: () => void
  className?: string
  // 高亮当前激活评论（面板侧常亮）
  isActive?: boolean
  // 激活来源：'editor' 时触发卡片闪烁；'panel' 不闪烁
  activeChangeSource?: 'panel' | 'editor'
  members?: Array<{ id: string; name?: string | null; email?: string | null }>
  // Show minimized view (header only, expandable)
  isMinimized?: boolean
  isLocked?: boolean
  allowEdit?: boolean
  allowDelete?: boolean
  allowCopyLink?: boolean
  allowResolve?: boolean
  alwaysShowReplyEditor?: boolean
}

export function CommentThread({
  comment,
  onResolve,
  onDelete,
  onReply,
  onEditComment,
  onApplySuggestion,
  onDismissCheck,
  onPromoteToTask: _onPromoteToTask,
  onClick,
  className,
  isActive = false,
  activeChangeSource = 'editor',
  members = [],
  isMinimized = false,
  isLocked = false,
  allowEdit = true,
  allowDelete = true,
  allowCopyLink = true,
  allowResolve = true,
  alwaysShowReplyEditor = false,
}: CommentThreadProps) {
  const rootRef = useRef<HTMLDivElement | null>(null)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null)

  const isResolved =
    comment.commentStatus === 'RESOLVED' ||
    comment.commentStatus === 'DISMISSED' ||
    comment.commentStatus === 'IMPLEMENTED'

  // Ensure/update flash style (brighter and more noticeable)
  const ensureFlashStyle = () => {
    const styleId = 'dg-card-flash-style'
    let style = document.getElementById(styleId) as HTMLStyleElement | null
    if (!style) {
      style = document.createElement('style')
      style.id = styleId
      document.head.appendChild(style)
    }
    // 更亮的闪烁：峰值 0.45，衰减到 0.24，再归零
    style.textContent = `@keyframes dgCardFlash { 0% { background: rgba(255, 212, 0, 0.00); } 20% { background: rgba(255, 212, 0, 0.45); } 60% { background: rgba(255, 212, 0, 0.24); } 100% { background: rgba(255, 212, 0, 0.00); } } .dg-card-flash { animation: dgCardFlash 900ms ease-in-out; }`
  }

  const triggerFlash = () => {
    ensureFlashStyle()
    const el = rootRef.current
    if (!el) return
    el.classList.remove('dg-card-flash')
    // Force reflow to restart animation
     
    void el.offsetWidth
    el.classList.add('dg-card-flash')
    window.setTimeout(() => {
      try {
        el.classList.remove('dg-card-flash')
      } catch {
        /* noop */
      }
    }, 950)
  }

  // Flash when becomes active
  useEffect(() => {
    if (isActive && activeChangeSource === 'editor') triggerFlash()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive])

  const handleDelete = (commentId: string) => {
    setPendingDeleteId(commentId)
    setDeleteDialogOpen(true)
  }

  return (
    <>
      <div
        className={cn(
          'border-foreground/10 border-b [&:last-child]:border-b-0',
          className,
          {
            'cursor-pointer': onClick && !isResolved,
          }
        )}
        data-comment-id={comment.id}
        onClick={isResolved ? undefined : onClick}
        aria-disabled={isResolved || undefined}
        ref={rootRef}
        style={
          isActive ? { backgroundColor: 'rgba(255, 212, 0, 0.067)' } : undefined
        }
      >
        <CommentContent
          comment={comment}
          onResolve={onResolve}
          onDelete={handleDelete}
          onReply={onReply}
          onEditComment={onEditComment}
          onApplySuggestion={onApplySuggestion}
          onDismissCheck={onDismissCheck}
          members={members}
          showActions={true}
          isMinimized={isMinimized}
          isLocked={isLocked}
          allowEdit={allowEdit}
          allowDelete={allowDelete}
          allowCopyLink={allowCopyLink}
          allowResolve={allowResolve}
          alwaysShowReplyEditor={alwaysShowReplyEditor}
        />
      </div>
      <ConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={(open) => {
          setDeleteDialogOpen(open)
          if (!open) setPendingDeleteId(null)
        }}
        title='Delete comment'
        desc='Are you sure you want to delete this comment?'
        destructive
        confirmText='Delete'
        handleConfirm={() => {
          if (pendingDeleteId) onDelete?.(pendingDeleteId)
          setDeleteDialogOpen(false)
          setPendingDeleteId(null)
        }}
      />
    </>
  )
}
